import React, { FC } from "react";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Snackbar,
  SnackbarContent,
  Button
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { FallbackProps } from "react-error-boundary";

export const ErrorPage: FC<FallbackProps> = ({ error, resetErrorBoundary }) => {
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);

  const handleCopy = () => {
    if (error?.stack) {
      navigator.clipboard.writeText(error.stack);
      setSnackbarOpen(true);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flex: 1,
        justifyContent: "center",
        alignItems: "center",
        p: 3,
        bgcolor: (theme) => theme.palette.MFT_DARK_BLUE
      }}
    >
      <Paper
        elevation={5}
        sx={{
          p: 4,
          maxWidth: 800,
          position: "relative",
          textAlign: "center",
          bgcolor: (theme) => theme.palette.MFT_OFF_WHITE
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="center">
          <ErrorOutlineIcon color="error" sx={{ fontSize: 50, mb: 2, mr: 2 }} />
          <Typography variant="h5" gutterBottom>
            Something went wrong
          </Typography>
        </Box>
        <Typography variant="body1" color="textSecondary" gutterBottom>
          {error?.message || "An unexpected error occurred."}
        </Typography>
        <Box>
          <Typography variant="body1" color="textSecondary" gutterBottom>
            Try reloading the page. If the error persist copy the stack trace
            and send to IT/TECH
          </Typography>
        </Box>

        {error?.stack && (
          <Box sx={{ mt: 3, textAlign: "left", position: "relative" }}>
            <Typography
              variant="body2"
              component="pre"
              sx={{
                maxHeight: 300,
                overflow: "auto",
                bgcolor: "#e0e0e0",
                p: 2,
                borderRadius: 1,
                whiteSpace: "pre-wrap"
              }}
            >
              {error.stack}
            </Typography>
            <Tooltip title="Copy stack trace">
              <IconButton
                onClick={handleCopy}
                sx={{ position: "absolute", top: 8, right: 8 }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        <Button
          variant="outlined"
          sx={{ mt: "16px" }}
          onClick={resetErrorBoundary}
        >
          Try reset error
        </Button>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={2000}
          onClose={() => setSnackbarOpen(false)}
        >
          <SnackbarContent
            message="Stack trace copied"
            sx={{
              bgcolor: (theme) => theme.palette.MFT_GREEN,
              color: (theme) => theme.palette.common.black
            }}
          />
        </Snackbar>
      </Paper>
    </Box>
  );
};
